// Admin-only speed data review (gateway /admin/traffic/*).
import { apiClient } from './api';

export type CollectionState = 'off' | 'dry_run' | 'on';
export interface CityCoverage { batches: number; usable: number; withheld: number }
export interface TrafficReport {
  cities?: Record<string, CityCoverage>;
  flagsRaised?: Record<string, number>;
  intake?: { accepted: number; rejected: number; rejectedShare: number };
  error?: string;
}
export interface TrafficSummary {
  switch: { state: CollectionState; changedBy: string | null; changedAt: number | null };
  days: { day: string; byReason: Record<string, { batches: number; segments: number }> }[];
  reports: { day: string; status: string; report: TrafficReport }[];
  openFlags: number;
  exclusions: number;
  matching?: { day: string; matched: number; unmatched: number; failed: number; skipped: number }[];
}
export interface TrafficFlag {
  id: string; res: number; cell: string; check_name: string; detail: Record<string, number>;
  week_start: number; status: string; created_at: number; resolved_at: number | null; resolved_by: string | null;
}
export interface TrafficCell { cell: string; kph: number; typicalKph: number }
export interface TrafficCellDetail {
  usable: boolean;
  profileHourly: (number | null)[];
  weeks: { weekStart: number; kph: number | null; batches: number }[];
  exclusions: TrafficExclusion[];
}
/** One ~100 m piece of a road edge, drawn in its real shape in the direction of travel. */
export interface TrafficRoad {
  edge: number; piece: number; coords: [number, number][];
  kph: number; typicalKph: number; refKph: number | null; name: string | null; roadClass: string | null;
}
export interface TrafficRoadDetail {
  edge: number; wayId: number; name: string | null; roadClass: string | null; lengthM: number; refKph: number | null; graph: string;
  pieces: { piece: number; typicalKph: number; hourly: (number | null)[]; batches: number; stoppedShare: number }[];
  weeks: { weekStart: number; kph: number | null; batches: number }[];
}
export interface TrafficExclusion { id: string; res: number; cell: string; from_ts: number | null; to_ts: number | null; reason: string; admin: string; created_at: number }

export const trafficApi = {
  summary: (days = 14): Promise<TrafficSummary> => apiClient.get('/admin/traffic/summary', { params: { days } }).then(r => r.data),
  flags: (status: 'open' | 'cleared' | 'excluded' = 'open'): Promise<TrafficFlag[]> =>
    apiClient.get('/admin/traffic/flags', { params: { status } }).then(r => r.data.flags),
  clearFlag: (id: string): Promise<void> => apiClient.post(`/admin/traffic/flags/${id}/clear`, {}).then(() => undefined),
  excludeFlag: (id: string, reason: string, from?: number, to?: number): Promise<void> =>
    apiClient.post(`/admin/traffic/flags/${id}/exclude`, { reason, from, to }).then(() => undefined),
  cells: (bbox: [number, number, number, number], res: number, how: number | null): Promise<TrafficCell[]> =>
    apiClient.get('/admin/traffic/cells', { params: { bbox: bbox.join(','), res, ...(how == null ? {} : { how }) } }).then(r => r.data.cells),
  cell: (res: number, cell: string): Promise<TrafficCellDetail> => apiClient.get(`/admin/traffic/cell/${res}/${cell}`).then(r => r.data),
  roads: (bbox: [number, number, number, number], how: number | null): Promise<TrafficRoad[]> =>
    apiClient.get('/admin/traffic/roads', { params: { bbox: bbox.join(','), ...(how == null ? {} : { how }) } }).then(r => r.data.roads),
  road: (edge: number): Promise<TrafficRoadDetail> => apiClient.get(`/admin/traffic/road/${edge}`).then(r => r.data),
  exclusions: (): Promise<TrafficExclusion[]> => apiClient.get('/admin/traffic/exclusions').then(r => r.data.exclusions),
  removeExclusion: (id: string): Promise<void> => apiClient.delete(`/admin/traffic/exclusions/${id}`).then(() => undefined),
  setSwitch: (state: CollectionState): Promise<void> => apiClient.put('/admin/traffic/switch', { state }).then(() => undefined),
};

/** Hour of week as used by the engine (UTC, epoch-aligned), shown in Lagos time. */
export function hourOfWeekLabel(how: number): string {
  const d = new Date((how + 1) * 3600 * 1000); // epoch 0 is Thursday 00:00 UTC; +1 h for Lagos
  const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()];
  return `${day} ${String(d.getUTCHours()).padStart(2, '0')}:00`;
}
